import React from "react";
import { useQuery } from "@tanstack/react-query";
import type { BankingPolicyValue } from "../../../../../../shared/frontend/admin/AdminBankingPolicySection";
import { BankAccessDialog, type BankAccessBody, type BankChallengeTransport, type BankingChallenge } from "../../../../../../shared/frontend/banking/BankAccessDialog";
import { useAuth } from "../../app/auth/useAuth";
import { useI18n } from "../../app/i18n/useI18n";
import { useRealtimeEvent, useRealtimeListRefresh, useRealtimeStatus } from "../../app/realtime/RealtimeContext";
import { RealtimeEventTypes } from "../../app/realtime/realtimeEventTypes";
import { useBankingOperationRun, type BankingOperationStatusView } from "../../../../../../shared/frontend/banking/useBankingOperationRun";

/** Fehlerschlüssel „Bankzugang nicht entsperrbar“ (Übersetzung `E_BankingCredentials`). */
const BANKING_CREDENTIALS_ERROR_KEY = "E_BankingCredentials";

export type { BankAccessBody } from "../../../../../../shared/frontend/banking/BankAccessDialog";

export type StatementConnection = {
  id: string; bankAccountId: string; bankAccountName: string; isEnabled: boolean;
  accessMethod: 0 | 1 | 2; hasStoredPassport: boolean; name?: string; hasStoredPin?: boolean;
  statementFetchFrom?: string | null;
};
export type StatementTransaction = { date: string; amount: string; purpose: string; externalId?: string | null;
  chargebackReasonCode?: string | null; isPotentialContribution?: boolean };
type Result = { success: boolean; message: string; transactions?: StatementTransaction[]; bankCodes?: string | null };

/** Bankauftrag (Lastschriftlauf, Überweisungen): Titel, Zusammenfassung und Endpunkt kommen vom Aufrufer. */
export type BankOrderRequest = {
  title: string;
  okText: string;
  summary?: React.ReactNode;
  submit: (body: BankAccessBody, signal: AbortSignal) => Promise<Result>;
};

/** m1member-Anbindung des gemeinsamen Bankdialogs: Endpunkte, Echtzeit-Hinweis und Texte. */
export function BankStatementAccessDialog({ open, connection, onClose, onBusy, onResult, purpose = "statement", order, reselectSecurityMethod }: {
  purpose?: "statement" | "test" | "order"; open: boolean; connection: StatementConnection | null; onClose: () => void;
  onBusy: (busy: boolean) => void; onResult: (result: Result) => void; order?: BankOrderRequest; reselectSecurityMethod?: boolean;
}) {
  const challengeRoot = purpose === "test" ? "/api/v1/bank-online-connections" : "/api/v1/finance/bank-statement";
  const { api, token } = useAuth();
  const { text } = useI18n();
  const [challengeSignal, setChallengeSignal] = React.useState(0);
  const policy = useQuery({
    queryKey: ["bank-online-policy"],
    queryFn: () => api.get<BankingPolicyValue>("/api/v1/bank-online-connections/policy"),
    enabled: open
  });
  useRealtimeEvent<{ bankAccountId?: string }>(RealtimeEventTypes.BankStatementTanRequired, (event) => {
    if (event.payload?.bankAccountId === connection?.bankAccountId) setChallengeSignal((value) => value + 1);
  }, { enabled: !!token && open });
  const transport = React.useMemo<BankChallengeTransport>(() => ({
    load: (bankAccountId, operationId) => api.get<BankingChallenge | null>(
      `${challengeRoot}/challenge?bankAccountId=${encodeURIComponent(bankAccountId)}&operationId=${encodeURIComponent(operationId)}`),
    answer: async (requestId, value) => { await api.post(`${challengeRoot}/challenge`, { requestId, value }); },
  }), [api, challengeRoot]);
  // Abruf und Test laufen im Hintergrund des Servers; das Ergebnis kommt über das Echtzeit-Ereignis.
  const [operationSignal, setOperationSignal] = React.useState(0);
  const operationConnected = useRealtimeStatus() === "connected";
  useRealtimeListRefresh({ eventTypes: [], reload: () => { setChallengeSignal(value => value + 1); setOperationSignal(value => value + 1); },
    enabled: !!token && open, background: false, debounceMs: 0 });
  useRealtimeEvent(RealtimeEventTypes.BankingOperationChanged, () => setOperationSignal((value) => value + 1), { enabled: !!token && open });
  const connectionId = connection?.id;
  const connectionAccountId = connection?.bankAccountId;
  const operations = React.useMemo(() => ({
    // ApiClient does not repeat POST after network/server errors. Its startup-migration
    // retry is limited to middleware rejections before the bank controller has run.
    start: (access: BankAccessBody) => api.post(purpose === "test"
      ? `/api/v1/bank-online-connections/${encodeURIComponent(connectionId ?? "")}/fints-test` : "/api/v1/finance/bank-statement/fetch",
      { bankAccountId: connectionAccountId, connectionId, ...access }, { suppressGlobalErrorToast: true }),
    status: (operationId: string) => api.get<BankingOperationStatusView<Result> | null>(
      `/api/v1/bank-online-connections/operations/${encodeURIComponent(operationId)}`, { suppressGlobalErrorToast: true }),
    cancel: (operationId: string) => api.post(`/api/v1/bank-online-connections/operations/${encodeURIComponent(operationId)}/cancel`, undefined, { suppressGlobalErrorToast: true }),
    toError: (status: BankingOperationStatusView<Result>) => new Error(text(status.errorKey ?? "E_BankingFailed")),
  }), [api, purpose, connectionId, connectionAccountId, text]);
  const runInBackground = useBankingOperationRun<Result>({ ...operations, signal: operationSignal, connected: operationConnected });
  const run = (access: BankAccessBody, signal: AbortSignal) => purpose === "order" && order
    ? order.submit(access, signal)
    : runInBackground.run(access, signal);
  const label = purpose === "order" && order ? order.okText : text(purpose === "test" ? "Banking test connection" : "Fetch statement");
  return <BankAccessDialog open={open} connection={connection} title={reselectSecurityMethod ? text("banking.changeSecurityMethod") : purpose === "order" && order ? order.title : label} okText={label}
    summary={purpose === "order" ? order?.summary : undefined} run={run} transport={transport} challengeSignal={challengeSignal + (operationConnected ? 1 : 0)}
    onClose={onClose} onBusy={onBusy} onResult={onResult} onAnswerError={(message) => onResult({ success: false, message })}
    tr={text} idPrefix="bank-statement" background={purpose !== "order"}
    startWithStoredCredentials={purpose === "statement"}
    statementFetchFrom={purpose === "statement" ? connection?.statementFetchFrom ?? null : undefined}
    reselectSecurityMethod={reselectSecurityMethod}
    cancelOperation={purpose !== "order" ? runInBackground.cancel : undefined}
    maxCredentialAttempts={policy.data?.maxCredentialAttempts}
    isCredentialsRejected={(outcome) => "result" in outcome && !outcome.result.success && outcome.result.message === BANKING_CREDENTIALS_ERROR_KEY}
    onAttemptsExceeded={() => onResult({ success: false, message: "banking.attemptsExceeded" })} />;
}
